import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import DataTable from "../../lib/components/DataTable";
import { getColumnsV2 } from "./constantV2.js";
import Pagination from "../../lib/components/Pagination/Pagination.jsx";
import useHybridSearch from "./hooks/useHybridSearch.js";
import { useGlobalFilters } from "../../lib/context/GlobalFilterProvider.jsx";
import SlideOutPanel from "../ocpp_messages/SlideOutPanel.jsx";
import MultiSelect from "../../lib/components/MultiSelect/MultiSelect.jsx";
import {
  Settings,
  SlidersHorizontal,
  Search,
  Download,
  X,
  Bookmark,
  BookmarkCheck,
  Rows3,
  Rows4,
  AlignJustify,
  Filter,
} from "lucide-react";
import { Button, Checkbox, Tooltip } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import useIsDarkTheme from "../../lib/hooks/useIsDarkTheme";
import { FEATURES, FeatureGate } from "../../lib/utils/featureAccess";
import { sanitizeRequestData } from "../../lib/utils/apiValidation";
import { EnhancedFilterRow } from "../../lib/components/EnhancedFilterRow";

// ── Map column filter keys to hybrid API filter field names (snake_case) ──
const CAMEL_TO_SNAKE = {
  serialNumber: "serial_number",
  siteId: "site_id",
  displayName: "display_name",
  stationName: "station_name",
  vendorName: "vendor_name",
  modelName: "model_name",
  partNum: "part_num",
  country: "country",
  protocol: "protocol",
  protocolVersion: "protocol_version",
  commissioningStatus: "commissioning_status",
  mode: "mode",
  dlbSource: "dlb_source",
  timezone: "timezone",
  phaseCount: "phase_count",
  maxCurrent: "max_current",
  breakerCurrent: "breaker_current",
  operatingVoltage: "operating_voltage",
  isConnected: "is_connected",
  isRetired: "is_retired",
  isFaulted: "is_faulted",
  dlbEnabled: "dlb_enabled",

  isOffPeakEligible: "is_off_peak_eligible",
  isDefaultOffPeakEligible: "is_default_off_peak_eligible",
  isDeviceReady: "is_device_ready",
  isOffGrid: "is_off_grid",
  hoControl: "ho_control",
  sku: "sku",
  fwVersion: "fw_version",
  operationStatus: "operation_status",
  chargingStatus: "charging_status",
  connectorIsPluggedIn: "connector_is_plugged_in",
  connectorStatusReason: "connector_status_reason",
  gatewayConnectivityStatus: "gateway_connectivity_status",
  sessionState: "session_state",
  lastReportedAt: "last_reported_at",
  createdAt: "created_at",
  updatedAt: "updated_at",
  city: "city",
  state: "state",
  zip: "zip",
  owner: "owner",
  phaseSwitchConfig: "phase_switch_config",
  sessionAuthentication: "session_authentication",
  rfidSessionAuthentication: "rfid_session_authentication",
  defaultChargeLevel: "default_charge_level",
  connectorLock: "connector_lock",
  chargingOptMode: "charging_opt_mode",
  systemProfile: "system_profile",
  hasUserSchedule: "has_user_schedule",
  userScheduleType: "user_schedule_type",
  localGreenCharging: "local_green_charging",
  useBattery: "use_battery",
  stormActive: "storm_active",
};

const FLEET_OS_VIEW_STATE_KEY = "fleet-os-view-state";

function loadViewState() {
  try {
    const raw = sessionStorage.getItem(FLEET_OS_VIEW_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Fleet List V2 — uses the hybrid search API (/api/home/search/hybrid)
 * with server-side pagination, filtering, sorting, and text search.
 */
export default function FleetMetricsOpenSearch() {
  const { serialNumber, siteId, setState } = useGlobalFilters();
  const location = useLocation();
  const navigate = useNavigate();
  const isDark = useIsDarkTheme();
  const searchInputRef = useRef(null);
  const filterDebounceRef = useRef(null);
  const drillDownAppliedRef = useRef(false);

  // Restore persisted view state across navigations
  const savedViewState = useRef(loadViewState()).current;

  // Default filter: exclude retired chargers
  const DEFAULT_FILTERS = useMemo(() => ({ isRetired: "false" }), []);
  const DEFAULT_API_FILTERS = useMemo(() => ({ is_retired: false }), []);

  // Refs to hold latest values — avoids stale closures in debounced callbacks
  const filtersRef = useRef(savedViewState?.filters ?? DEFAULT_FILTERS);
  const quickSearchRef = useRef(savedViewState?.quickSearch ?? "");
  const sortConfigRef = useRef(savedViewState?.sortConfig ?? { key: null, direction: "asc" });

  // ── Parse drill-down filters from URL query params ──
  const urlFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const f = {};
    for (const [key, value] of params.entries()) {
      // Boolean conversions
      if (value === "true") f[key] = true;
      else if (value === "false") f[key] = false;
      else f[key] = value;
    }
    return f;
  }, [location.search]);

  // ── Hybrid search state ──
  // Determine initial params: URL drill-down filters take priority, then restored session state
  const initialSearchParams = useMemo(() => {
    if (Object.keys(urlFilters).length > 0) {
      return { page: 1, size: 25, filters: urlFilters };
    }
    if (savedViewState?.searchParams) {
      return savedViewState.searchParams;
    }
    return { page: 1, size: 25, filters: DEFAULT_API_FILTERS };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only compute once on mount

  const {
    data: tableData,
    total: totalItems,
    totalPages,
    isLoading,
    isError,
    searchParams,
    setSearchParams,
  } = useHybridSearch(initialSearchParams);

  // ── Local UI state (restored from session) ──
  const [quickSearch, setQuickSearchRaw] = useState(savedViewState?.quickSearch ?? "");
  const setQuickSearch = useCallback(v => {
    quickSearchRef.current = v;
    setQuickSearchRaw(v);
  }, []);
  const [filters, setFiltersRaw] = useState(savedViewState?.filters ?? DEFAULT_FILTERS);
  const setFilters = useCallback(newFilters => {
    if (!newFilters || typeof newFilters !== "object") return;
    const sanitized = sanitizeRequestData(newFilters);
    filtersRef.current = sanitized;
    setFiltersRaw(sanitized);
  }, []);
  const [sortConfig, setSortConfigRaw] = useState(savedViewState?.sortConfig ?? { key: null, direction: "asc" });
  const setSortConfig = useCallback(v => {
    sortConfigRef.current = v;
    setSortConfigRaw(v);
  }, []);
  const [showFilters, setShowFilters] = useState(savedViewState?.showFilters ?? false);

  // Persist view state to sessionStorage on changes
  useEffect(() => {
    const viewState = { filters, quickSearch, sortConfig, showFilters, searchParams };
    sessionStorage.setItem(FLEET_OS_VIEW_STATE_KEY, JSON.stringify(viewState));
  }, [filters, quickSearch, sortConfig, showFilters, searchParams]);
  const [showAppliedFilters, setShowAppliedFilters] = useState(true);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [density, setDensity] = useState("comfortable");
  const [openChargerDetailsSlideout, setOpenChargerDetailsSlideout] = useState(false);
  const [, setPreviewRow] = useState(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  // Saved views
  const [savedViews, setSavedViews] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("fleet-v2-saved-views") || "[]");
    } catch {
      return [];
    }
  });
  const [showSavedViews, setShowSavedViews] = useState(false);

  // ── Visible columns ──
  const DEFAULT_VISIBLE_KEYS = useMemo(
    () => [
      "serialNumber",
      "siteId",
      "displayName",
      "vendorName",
      "country",
      "isConnected",
      "sessionState",
      "lastReportedAt",
      "fwVersion",
    ],
    []
  );

  const [visibleColumns, setVisibleColumns] = useState(() => {
    return getColumnsV2().reduce((acc, col) => {
      acc[col.key] = DEFAULT_VISIBLE_KEYS.includes(col.key);
      return acc;
    }, {});
  });

  useEffect(() => {
    setVisibleColumns(prev => {
      const next = { ...prev };
      getColumnsV2().forEach(col => {
        if (!(col.key in next)) {
          next[col.key] = DEFAULT_VISIBLE_KEYS.includes(col.key);
        }
      });
      return next;
    });
  }, [DEFAULT_VISIBLE_KEYS]);

  // ── Profile menu outside click ──
  useEffect(() => {
    if (!isProfileMenuOpen) return;
    const handleOutsideClick = event => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    window.addEventListener("mousedown", handleOutsideClick);
    return () => window.removeEventListener("mousedown", handleOutsideClick);
  }, [isProfileMenuOpen]);

  const handleColumnVisibilityToggle = key => {
    setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Columns ──
  const columns = getColumnsV2((serialNum, chargerSiteId) => {
    setState({ serialNumber: serialNum, siteId: chargerSiteId, isSerialNumberConfirmed: true });
    navigate(`/chargers/${serialNum}`);
  });

  const BOOL_KEYS = useMemo(
    () =>
      new Set([
        "isConnected",
        "isRetired",
        "isFaulted",
        "dlbEnabled",
        "isOffPeakEligible",
        "isDefaultOffPeakEligible",
        "isDeviceReady",
        "isOffGrid",
        "hoControl",
        "connectorIsPluggedIn",
        "connectorLock",
        "hasUserSchedule",
        "localGreenCharging",
        "useBattery",
        "stormActive",
      ]),
    []
  );

  // ── Build API filters from UI column filters ──
  // Returns { apiFilters, dateField, fromDate, toDate }
  const buildApiFilters = useCallback(
    filterObj => {
      const apiFilters = {};
      let dateField = "";
      let fromDate = "";
      let toDate = "";

      Object.entries(filterObj).forEach(([key, value]) => {
        // Handle range filters (like maxCurrent)
        if (
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value) &&
          (value.min !== undefined || value.max !== undefined)
        ) {
          const snakeKey = CAMEL_TO_SNAKE[key];
          if (!snakeKey) return;

          // Send min and max as separate filters (only if they have values)
          if (value.min !== "" && value.min !== undefined) {
            apiFilters[`${snakeKey}_min`] = Number(value.min);
          }
          if (value.max !== "" && value.max !== undefined) {
            apiFilters[`${snakeKey}_max`] = Number(value.max);
          }
          return;
        }

        // Handle multi-select array values
        if (Array.isArray(value)) {
          const snakeKey = CAMEL_TO_SNAKE[key];
          if (!snakeKey || value.length === 0) return;
          apiFilters[snakeKey] = value;
          return;
        }

        const trimmed = String(value).trim();
        if (!trimmed) return;
        const snakeKey = CAMEL_TO_SNAKE[key];
        if (!snakeKey) return;

        // Handle date range presets (lastReportedAt, createdAt, updatedAt)
        // Multiple date filters are now supported - they work with AND logic (intersection)
        if (["lastReportedAt", "createdAt", "updatedAt"].includes(key)) {
          const now = new Date();
          let startDate;

          let endDate = now;

          switch (value) {
            case "today":
              // "Today" means from the beginning of today (00:00:00) to now
              startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              // endDate is already set to 'now' above
              break;
            case "last_7_days":
              startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              break;
            case "last_30_days":
              startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
              break;
            case "last_90_days":
              startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
              break;
            case "last_year":
              startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
              break;
            default:
              return;
          }

          // Use API's date range parameters with __from and __to suffixes
          // Send as ISO timestamp format so API can filter to exact time
          // Multiple date fields are added directly to apiFilters with __from/__to suffixes
          apiFilters[snakeKey + "__from"] = startDate.toISOString();
          apiFilters[snakeKey + "__to"] = endDate.toISOString();

          // Keep track of the last date field for backward compatibility
          dateField = snakeKey;
          fromDate = startDate.toISOString();
          toDate = endDate.toISOString();
        } else if (BOOL_KEYS.has(key)) {
          const lower = trimmed.toLowerCase();
          if (["true", "yes", "1", "connected", "online"].includes(lower)) {
            apiFilters[snakeKey] = true;
          } else if (["false", "no", "0", "disconnected", "offline"].includes(lower)) {
            apiFilters[snakeKey] = false;
          }
        } else if (["operatingVoltage", "phaseCount"].includes(key)) {
          // Convert numeric SELECT filters to numbers
          const num = Number(trimmed);
          if (!isNaN(num)) {
            apiFilters[snakeKey] = num;
          } else {
            apiFilters[snakeKey] = trimmed;
          }
        } else {
          apiFilters[snakeKey] = trimmed;
        }
      });

      return { apiFilters, dateField, fromDate, toDate };
    },
    [BOOL_KEYS]
  );

  // ── Sync UI state → server search params ──
  // Reads from refs to avoid stale closures; overrides take precedence.
  const syncSearchParams = useCallback(
    ({ filtersOverride, searchOverride, sortOverride, pageOverride } = {}) => {
      const currentFilters = filtersOverride !== undefined ? filtersOverride : filtersRef.current;
      const currentSearch = searchOverride !== undefined ? searchOverride : quickSearchRef.current;
      const currentSort = sortOverride !== undefined ? sortOverride : sortConfigRef.current;

      const { apiFilters } = buildApiFilters(currentFilters);
      const sortStr = currentSort.key
        ? `${CAMEL_TO_SNAKE[currentSort.key] || currentSort.key}:${currentSort.direction}`
        : "";

      // Note: Date range filters are now added directly in buildApiFilters with __from/__to suffixes
      // Multiple date filters are supported and work with AND logic (intersection)

      setSearchParams(prev => ({
        ...prev,
        query: currentSearch.trim(),
        filters: apiFilters,
        sort: sortStr,
        page: pageOverride !== undefined ? pageOverride : 1,
      }));
    },
    [buildApiFilters, setSearchParams]
  );

  // Debounced filter/search sync (1s after last keystroke)
  const debouncedSync = useCallback(
    (overrides = {}) => {
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
      filterDebounceRef.current = setTimeout(() => {
        syncSearchParams(overrides);
      }, 1000);
    },
    [syncSearchParams]
  );

  // Flush: cancel pending debounce and sync immediately (e.g. on Enter key)
  const flushSync = useCallback(
    (overrides = {}) => {
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
      syncSearchParams(overrides);
    },
    [syncSearchParams]
  );

  // ── Drill-down from Dashboard ──
  useEffect(() => {
    const drillDownFilters = location.state?.drillDownFilters;
    if (drillDownFilters && !drillDownAppliedRef.current) {
      drillDownAppliedRef.current = true;
      const uiFilters = {};
      Object.entries(drillDownFilters).forEach(([key, value]) => {
        uiFilters[key] = String(value);
      });
      setFilters(uiFilters);
      setShowFilters(true);
      syncSearchParams({ filtersOverride: uiFilters });
      window.history.replaceState({}, "");
    }
  }, [location.state, setFilters, syncSearchParams]);

  // ── Cmd+K shortcut ──
  useEffect(() => {
    const handler = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    };
  }, []);

  // ── Pagination handlers ──
  const handlePageChange = newPage => {
    setSearchParams(prev => ({ ...prev, page: newPage + 1 })); // API is 1-indexed, UI is 0-indexed
    setSelectedRows(new Set());
  };

  const handleRowsPerPageChange = newRowsPerPage => {
    setSearchParams(prev => ({ ...prev, size: newRowsPerPage, page: 1 }));
    setSelectedRows(new Set());
  };

  // ── Sort handler — triggers server-side sort immediately (no debounce) ──
  const handleSortChange = useCallback(
    newSortConfig => {
      setSortConfig(newSortConfig);
      flushSync({ sortOverride: newSortConfig });
    },
    [setSortConfig, flushSync]
  );

  // ── Filter change handler — debounced server-side filter ──
  const handleFiltersChange = useCallback(
    newFilters => {
      setFilters(newFilters);
      debouncedSync({ filtersOverride: newFilters });
    },
    [setFilters, debouncedSync]
  );

  // ── Filter Enter key — immediately submit filters ──
  const handleFilterKeyDown = useCallback(
    e => {
      if (e.key === "Enter") {
        e.preventDefault();
        flushSync();
      }
    },
    [flushSync]
  );

  // ── Quick search handler ──
  const handleQuickSearchChange = useCallback(
    value => {
      setQuickSearch(value);
      debouncedSync({ searchOverride: value });
    },
    [setQuickSearch, debouncedSync]
  );

  // ── Quick search Enter key — immediately submit ──
  const handleQuickSearchKeyDown = useCallback(
    e => {
      if (e.key === "Enter") {
        e.preventDefault();
        flushSync();
      }
    },
    [flushSync]
  );

  // ── Row selection ──
  const toggleRowSelection = useCallback(serialNum => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(serialNum)) next.delete(serialNum);
      else next.add(serialNum);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedRows.size === tableData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(tableData.map(r => r.serialNumber)));
    }
  }, [selectedRows, tableData]);

  const isAllSelected = tableData.length > 0 && selectedRows.size === tableData.length;
  const isSomeSelected = selectedRows.size > 0 && selectedRows.size < tableData.length;

  // ── CSV Export ──
  const exportCSV = useCallback(
    (dataToExport = null) => {
      const rows = dataToExport || tableData;
      if (rows.length === 0) return;
      const exportCols = columns.filter(c => visibleColumns[c.key] !== false);
      const header = exportCols.map(c => `"${c.label}"`).join(",");
      const csvRows = rows.map(row =>
        exportCols
          .map(c => {
            const val = row?.[c.key];
            if (val == null) return "";
            return `"${String(val).replace(/"/g, '""')}"`;
          })
          .join(",")
      );
      const csv = [header, ...csvRows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fleet-search-v2-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    [tableData, columns, visibleColumns]
  );

  const exportSelected = useCallback(() => {
    const rows = tableData.filter(r => selectedRows.has(r.serialNumber));
    exportCSV(rows);
  }, [tableData, selectedRows, exportCSV]);

  // ── Active filter chips (includes drill-down URL filters) ──
  const activeFilters = useMemo(() => {
    const entries = [];
    // Server-side filters from URL drill-down
    // Build reverse snake→camel lookup for label resolution
    const SNAKE_TO_CAMEL = Object.fromEntries(Object.entries(CAMEL_TO_SNAKE).map(([c, s]) => [s, c]));
    const serverFilters = searchParams.filters || {};

    Object.entries(serverFilters).forEach(([key, value]) => {
      // Skip individual _min and _max filters - they'll be shown as combined range
      if (key.endsWith("_min") || key.endsWith("_max")) {
        return;
      }

      // Skip individual __from and __to filters - they're internal API format, not displayed
      if (key.endsWith("__from") || key.endsWith("__to")) {
        return;
      }

      if (value !== undefined && value !== null && String(value).trim()) {
        const camelKey = SNAKE_TO_CAMEL[key] || key;
        const col = columns.find(c => c.key === key || c.key === camelKey);
        const displayValue = value === true ? "Yes" : value === false ? "No" : String(value);
        entries.push({ key: `__server__${key}`, label: col?.label || key, value: displayValue, serverKey: key });
      }
    });
    // Client-side column filters (skip keys already shown as server filters)
    // First pass: collect numeric range filters (_min/_max pairs)
    const rangeFilters = new Set();
    Object.keys(filters).forEach(key => {
      if (key.endsWith("_min") || key.endsWith("_max")) {
        const baseKey = key.slice(0, -4);
        rangeFilters.add(baseKey);
      }
    });

    // Second pass: add filters to entries
    Object.entries(filters).forEach(([key, value]) => {
      if (value && (Array.isArray(value) ? value.length > 0 : String(value).trim())) {
        const snakeKey = CAMEL_TO_SNAKE[key] || key;
        // Check if this filter is already in server filters (use !== undefined to handle false/0 values)
        if (serverFilters[key] !== undefined || serverFilters[snakeKey] !== undefined) {
          return;
        }

        // Skip individual __from and __to filters - they're internal API format, not displayed
        if (key.endsWith("__from") || key.endsWith("__to")) {
          return;
        }

        // Skip individual _min and _max filters - they'll be shown as combined range
        if (key.endsWith("_min") || key.endsWith("_max")) {
          return;
        }

        const col = columns.find(c => c.key === key);

        // Special handling for multi-select arrays
        if (Array.isArray(value)) {
          const displayValue = value.join(", ");
          entries.push({ key, label: col?.label || key, value: displayValue });
          return;
        }

        // Special handling for range filters (min/max)
        if (typeof value === "object" && value !== null && (value.min !== undefined || value.max !== undefined)) {
          const minVal = value.min !== undefined && value.min !== "" ? value.min : null;
          const maxVal = value.max !== undefined && value.max !== "" ? value.max : null;

          if (minVal !== null || maxVal !== null) {
            let displayValue = "";
            if (minVal !== null && maxVal !== null) {
              displayValue = `${minVal} - ${maxVal}`;
            } else if (minVal !== null) {
              displayValue = `≥ ${minVal}`;
            } else if (maxVal !== null) {
              displayValue = `≤ ${maxVal}`;
            }
            entries.push({ key, label: col?.label || key, value: displayValue });
          }
          return;
        }

        // Special handling for date filters to show the preset label
        let displayValue = String(value);
        if (["lastReportedAt", "createdAt", "updatedAt"].includes(key)) {
          const presetLabels = {
            today: "Today",
            last_7_days: "Last 7 Days",
            last_30_days: "Last 30 Days",
            last_90_days: "Last 90 Days",
            last_year: "Last Year",
          };
          displayValue = presetLabels[value] || String(value);
        }

        entries.push({ key, label: col?.label || key, value: displayValue });
      }
    });
    if (quickSearch.trim()) {
      entries.push({ key: "__quickSearch", label: "Search", value: quickSearch.trim() });
    }
    return entries;
  }, [filters, quickSearch, columns, searchParams.filters]);

  // Remove individual filter
  const removeFilter = useCallback(
    key => {
      if (key === "__quickSearch") {
        setQuickSearch("");
        debouncedSync({ searchOverride: "" });
      } else if (key.startsWith("__server__")) {
        // Remove a server-side filter and update URL
        const serverKey = key.replace("__server__", "");

        // Also remove from local filters if it exists there (convert snake_case to camelCase)
        const SNAKE_TO_CAMEL = Object.fromEntries(Object.entries(CAMEL_TO_SNAKE).map(([c, s]) => [s, c]));
        const camelKey = SNAKE_TO_CAMEL[serverKey] || serverKey;
        const nextLocalFilters = { ...filters };
        delete nextLocalFilters[camelKey];
        delete nextLocalFilters[serverKey];
        setFilters(nextLocalFilters);

        setSearchParams(prev => {
          const nextFilters = { ...prev.filters };
          delete nextFilters[serverKey];
          return { ...prev, filters: nextFilters, page: 1 };
        });
        // Clean URL param
        const params = new URLSearchParams(location.search);
        params.delete(serverKey);
        navigate({ search: params.toString() }, { replace: true });
      } else {
        // Remove a client-side filter
        const next = { ...filters };
        delete next[key];
        setFilters(next);
        // Also remove from searchParams in case it's there
        const snakeKey = CAMEL_TO_SNAKE[key] || key;
        setSearchParams(prev => {
          const nextFilters = { ...prev.filters };
          delete nextFilters[key];
          delete nextFilters[snakeKey];

          // If removing any date filter, also clear date range keys with __from and __to suffixes
          if (["lastReportedAt", "createdAt", "updatedAt"].includes(key)) {
            delete nextFilters[snakeKey + "__from"];
            delete nextFilters[snakeKey + "__to"];
          }

          // Also handle removal of date range filters (key is the base field name)
          delete nextFilters[key + "__from"];
          delete nextFilters[key + "__to"];

          return { ...prev, filters: nextFilters, page: 1 };
        });
        // Immediately sync to prevent filter from reappearing
        flushSync({ filtersOverride: next });
      }
    },
    [filters, setFilters, setQuickSearch, debouncedSync, flushSync, setSearchParams, navigate, location.search]
  );

  // Clear all filters (resets to default: non-retired only)
  const clearAllFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setQuickSearch("");
    setSearchParams(prev => ({
      ...prev,
      filters: DEFAULT_API_FILTERS,
      query: "",
      page: 1,
    }));
    // Clear URL params
    navigate({ search: "" }, { replace: true });
  }, [setFilters, setQuickSearch, setSearchParams, navigate, DEFAULT_FILTERS, DEFAULT_API_FILTERS]);

  // ── Saved views ──
  const saveCurrentView = useCallback(() => {
    const name = prompt("Name this view:");
    if (!name) return;
    const view = { name, filters, sortConfig, visibleColumns, quickSearch, density };
    const next = [...savedViews, view];
    setSavedViews(next);
    localStorage.setItem("fleet-v2-saved-views", JSON.stringify(next));
  }, [filters, sortConfig, visibleColumns, quickSearch, density, savedViews]);

  const loadView = useCallback(
    view => {
      if (view.filters) setFilters(view.filters);
      if (view.sortConfig) setSortConfig(view.sortConfig);
      if (view.visibleColumns) setVisibleColumns(view.visibleColumns);
      if (view.quickSearch != null) setQuickSearch(view.quickSearch);
      if (view.density) setDensity(view.density);
      setShowSavedViews(false);
      // Sync to server immediately
      flushSync({
        filtersOverride: view.filters || {},
        searchOverride: view.quickSearch || "",
        sortOverride: view.sortConfig || { key: null, direction: "asc" },
      });
    },
    [setFilters, setSortConfig, setQuickSearch, flushSync]
  );

  const deleteView = useCallback(
    idx => {
      const next = savedViews.filter((_, i) => i !== idx);
      setSavedViews(next);
      localStorage.setItem("fleet-v2-saved-views", JSON.stringify(next));
    },
    [savedViews]
  );

  // ── Toolbar button style ──
  const toolbarBtnSx = (isActive = false) => ({
    textTransform: "none",
    borderColor: isActive ? "var(--color-accent-active)" : isDark ? "#374151" : "#d1d5db",
    borderRadius: "10px",
    color: isActive ? "var(--color-accent-active)" : isDark ? "#d1d5db" : "#374151",
    backgroundColor: isActive ? (isDark ? "var(--color-accent-light)" : "var(--color-accent-alpha-06)") : "transparent",
    fontWeight: 600,
    fontSize: "0.78rem",
    px: 1.5,
    py: 0.6,
    minWidth: "auto",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "var(--color-accent-active)",
      backgroundColor: isDark ? "var(--color-accent-alpha-08)" : "var(--color-accent-lightest)",
      color: "var(--color-accent-active)",
    },
  });

  // Density icon
  const densityIcon =
    density === "compact" ? (
      <Rows4 size={15} />
    ) : density === "spacious" ? (
      <AlignJustify size={15} />
    ) : (
      <Rows3 size={15} />
    );
  const nextDensity = density === "compact" ? "comfortable" : density === "comfortable" ? "spacious" : "compact";

  // Checkbox column
  const checkboxColumn = {
    key: "__select",
    label: "",
    sortable: false,
    filterable: false,
    width: "48px",
    render: (_, row) => (
      <Checkbox
        size="small"
        checked={selectedRows.has(row.serialNumber)}
        onClick={e => e.stopPropagation()}
        onChange={() => toggleRowSelection(row.serialNumber)}
        sx={{ p: 0, color: isDark ? "#4b5563" : "#d1d5db", "&.Mui-checked": { color: "var(--color-accent-active)" } }}
      />
    ),
  };

  const columnsWithCheckbox = [checkboxColumn, ...columns];
  const visibleColumnsWithCheckbox = { __select: true, ...visibleColumns };

  // API is 1-indexed, Pagination component is 0-indexed
  const currentPage = (searchParams.page || 1) - 1;

  return (
    <FeatureGate featureId={FEATURES.FLEET_SEARCH}>
      <div className="fleet-search">
        {/* Toolbar */}
        <motion.div
          className="fleet-search__toolbar"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="fleet-search__toolbar-left">
            <div className="fleet-search__search">
              <Search size={14} />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search serial, site, model, vendor, city..."
                value={quickSearch}
                onChange={e => handleQuickSearchChange(e.target.value)}
                onKeyDown={handleQuickSearchKeyDown}
              />
              <kbd className="fleet-search__kbd">{navigator.platform?.includes("Mac") ? "\u2318" : "Ctrl"}K</kbd>
            </div>
          </div>
          <div className="fleet-search__toolbar-right">
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowFilters(prev => !prev)}
              startIcon={<SlidersHorizontal size={15} />}
              sx={toolbarBtnSx(showFilters)}
            >
              {showFilters ? "Hide Filters" : "Filters"}
            </Button>
            {activeFilters.length > 0 && (
              <Tooltip title={showAppliedFilters ? "Hide applied filters" : "Show applied filters"} arrow>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowAppliedFilters(prev => !prev)}
                  startIcon={<Filter size={15} />}
                  sx={toolbarBtnSx(showAppliedFilters)}
                >
                  Applied ({activeFilters.length})
                </Button>
              </Tooltip>
            )}
            <MultiSelect
              options={columns}
              selectedValues={visibleColumns}
              onSelectionChange={handleColumnVisibilityToggle}
              buttonLabel="Columns"
              searchPlaceholder="Search columns..."
              emptyMessage="No columns found"
              buttonIcon={<Settings size={15} />}
              getOptionLabel={option => option.label}
              getOptionId={option => option.key}
            />
            <Tooltip title={`Density: ${density}`} arrow>
              <Button size="small" variant="outlined" onClick={() => setDensity(nextDensity)} sx={toolbarBtnSx()}>
                {densityIcon}
              </Button>
            </Tooltip>
            <Tooltip title="Export CSV" arrow>
              <Button
                size="small"
                variant="outlined"
                onClick={() => exportCSV()}
                startIcon={<Download size={15} />}
                sx={toolbarBtnSx()}
                disabled={totalItems === 0}
              >
                Export
              </Button>
            </Tooltip>
            {/* Saved views */}
            <div className="fleet-search__saved-views-wrap" ref={profileMenuRef}>
              <Tooltip title="Saved Views" arrow>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowSavedViews(prev => !prev)}
                  sx={toolbarBtnSx(showSavedViews)}
                >
                  {savedViews.length > 0 ? <BookmarkCheck size={15} /> : <Bookmark size={15} />}
                </Button>
              </Tooltip>
              <AnimatePresence>
                {showSavedViews && (
                  <motion.div
                    className="fleet-search__saved-views-dropdown"
                    initial={{ opacity: 0, y: -6, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.96 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="fleet-search__saved-views-header">
                      <span>Saved Views</span>
                      <button className="fleet-search__saved-views-save" onClick={saveCurrentView}>
                        + Save Current
                      </button>
                    </div>
                    {savedViews.length === 0 ? (
                      <div className="fleet-search__saved-views-empty">No saved views yet</div>
                    ) : (
                      savedViews.map((view, idx) => (
                        <div key={idx} className="fleet-search__saved-views-item">
                          <button className="fleet-search__saved-views-name" onClick={() => loadView(view)}>
                            {view.name}
                          </button>
                          <button className="fleet-search__saved-views-delete" onClick={() => deleteView(idx)}>
                            <X size={12} />
                          </button>
                        </div>
                      ))
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>

        {/* Active Filter Chips */}
        <AnimatePresence>
          {showAppliedFilters && activeFilters.length > 0 && (
            <motion.div
              className="fleet-search__filter-chips"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              {activeFilters.map(f => (
                <span key={f.key} className="fleet-search__filter-chip">
                  <span className="fleet-search__filter-chip-label">{f.label}:</span> {f.value}
                  <button className="fleet-search__filter-chip-remove" onClick={() => removeFilter(f.key)}>
                    <X size={11} />
                  </button>
                </span>
              ))}
              <button className="fleet-search__filter-chip-clear" onClick={clearAllFilters}>
                Clear All
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bulk Actions Bar */}
        <AnimatePresence>
          {selectedRows.size > 0 && (
            <motion.div
              className="fleet-search__bulk-bar"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              <span className="fleet-search__bulk-count">{selectedRows.size} selected</span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Download size={14} />}
                onClick={exportSelected}
                sx={toolbarBtnSx()}
              >
                Export Selected
              </Button>
              <button className="fleet-search__bulk-clear" onClick={() => setSelectedRows(new Set())}>
                <X size={13} /> Clear
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty State */}
        {!isLoading && tableData.length === 0 && (
          <motion.div
            className="fleet-search__empty-state"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="fleet-search__empty-icon">
              <Search size={40} />
            </div>
            <h3 className="fleet-search__empty-title">No Chargers Found</h3>
            <p className="fleet-search__empty-text">
              {isError
                ? "Failed to load charger data. Please try refreshing."
                : "Try adjusting your search or filters to find chargers."}
            </p>
          </motion.div>
        )}

        {/* Table */}
        {(tableData.length > 0 || isLoading) && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
          >
            <Pagination
              totalPages={totalPages > 0 ? totalPages : Math.max(1, Math.ceil(totalItems / searchParams.size))}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              rowsPerPage={searchParams.size}
              onRowsPerPageChange={handleRowsPerPageChange}
              totalItems={totalItems}
            />
            <DataTable
              columns={columnsWithCheckbox}
              data={tableData}
              visibleColumns={visibleColumnsWithCheckbox}
              filters={{}}
              onFiltersChange={handleFiltersChange}
              onFilterKeyDown={handleFilterKeyDown}
              sortConfig={sortConfig}
              onSortConfigChange={handleSortChange}
              loading={isLoading}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(prev => !prev)}
              density={density}
              selectAllChecked={isAllSelected}
              selectAllIndeterminate={isSomeSelected}
              onSelectAll={toggleSelectAll}
              filterRowComponent={props => <EnhancedFilterRow {...props} filters={filters} isDark={isDark} />}
            />
          </motion.div>
        )}

        {/* Slide-out preview */}
        {openChargerDetailsSlideout && (
          <SlideOutPanel
            open={openChargerDetailsSlideout}
            onClose={() => {
              setOpenChargerDetailsSlideout(false);
              setPreviewRow(null);
            }}
            serialNumber={serialNumber}
            siteId={siteId}
          />
        )}
      </div>
    </FeatureGate>
  );
}
